import { Package, BarChart3, Archive, LogOut, Maximize2, Minimize2, Settings, ShoppingBag, HelpCircle, Users } from "lucide-react";
import { useUserFeatures } from "@/hooks/useUserFeatures";
import { useOnboarding } from "@/hooks/useOnboarding";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { LanguageToggle } from "./LanguageToggle";
import { ThemeToggle } from "./ThemeToggle";
import { OrderFlowLogo } from "./OrderFlowLogo";
import { LogoutConfirmDialog } from "./LogoutConfirmDialog";
import { useNavigate, useLocation } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useLogout } from "@/hooks/useLogout";
import { useFullscreen } from "@/hooks/useFullscreen";
import { useSidebar } from "@/components/ui/sidebar";
import { useState, useCallback } from "react";

/**
 * MobileNav is now only used within the Sidebar's mobile Sheet.
 * This component provides the content for the mobile drawer.
 * 
 * NAVIGATION LAW: Close sheet FIRST, then navigate after animation (300ms).
 * This prevents animation collisions between Sheet close and page swap.
 */
export function MobileNavContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const { handleLogout } = useLogout();
  const { setShowOnboarding } = useOnboarding();
  const { isFullscreen, toggleFullscreen, isSupported: fullscreenSupported } = useFullscreen();
  const { setOpenMobile } = useSidebar();
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const { data: userFeatures } = useUserFeatures();
  const externalIntakeEnabled = userFeatures?.external_order_intake ?? false;
  const customersModuleEnabled = userFeatures?.customers_module ?? false;

  // Close sidebar first, then navigate after animation completes
  const handleNavigate = useCallback((path: string) => {
    setOpenMobile(false);
    setTimeout(() => navigate(path), 220); // Sheet animation is 200ms + small buffer
  }, [setOpenMobile, navigate]);

  const handleSignOut = () => {
    setShowLogoutDialog(true);
  };

  const confirmSignOut = async () => {
    await handleLogout();
    setShowLogoutDialog(false);
  };

  const workspaceItems = [
    { path: "/orders", icon: Package, label: t("nav.orders") },
    ...(customersModuleEnabled ? [{ path: "/customers", icon: Users, label: t("nav.customers") }] : []),
    ...(externalIntakeEnabled ? [{ path: "/products", icon: ShoppingBag, label: t("nav.products") }] : []),
    { path: "/dashboard", icon: BarChart3, label: t("nav.dashboard") },
    { path: "/archive", icon: Archive, label: t("nav.archive") },
  ];

  const configItems = [
    { path: "/settings", icon: Settings, label: t("nav.systemConfig") },
  ];
  
  // Handle Help click - opens onboarding modal via context, then close drawer
  const handleHelpClick = useCallback(() => {
    setOpenMobile(false);
    setTimeout(() => setShowOnboarding(true), 220);
  }, [setOpenMobile, setShowOnboarding]);

  return (
    <>
      <div className="flex flex-col h-full">
        {/* Logo - Links to /orders as panic button */}
        <div className="p-4 border-b border-border">
          <div onClick={() => handleNavigate("/orders")} className="cursor-pointer">
            <OrderFlowLogo size="small" showText={true} animated={false} onGradientBackground={false} />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto py-4">
          {/* Workspaces Section */}
          <div className="px-3 mb-2">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
              {t("nav.workspaces")}
            </p>
          </div>
          <div className="space-y-1 px-2">
            {workspaceItems.map((item) => (
              <Button
                key={item.path}
                variant={location.pathname === item.path ? "secondary" : "ghost"}
                className="justify-start text-foreground h-11 w-full"
                onClick={() => handleNavigate(item.path)}
              >
                <item.icon className="h-4 w-4 mr-3" />
                {item.label}
              </Button>
            ))}
          </div>

          {/* Configuration Section */}
          <div className="px-3 mt-6 mb-2">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
              {t("nav.configuration")}
            </p>
          </div>
          <div className="space-y-1 px-2">
            {configItems.map((item) => (
              <Button
                key={item.path}
                variant={location.pathname === item.path ? "secondary" : "ghost"}
                className="justify-start text-foreground h-11 w-full"
                onClick={() => handleNavigate(item.path)}
              >
                <item.icon className="h-4 w-4 mr-3" />
                {item.label}
              </Button>
            ))}
            
            {/* Help / Guide button */}
            <Button
              variant="ghost"
              className="justify-start text-foreground h-11 w-full"
              onClick={handleHelpClick}
            >
              <HelpCircle className="h-4 w-4 mr-3" />
              {t("nav.showGuide")}
            </Button>
          </div>

          {/* Separator between business and personal */}
          <Separator className="my-6" />

          {/* Personal Settings Section */}
          <div className="px-3 mb-2">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
              {t("nav.personalSettings")}
            </p>
          </div>
          <div className="space-y-3 px-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{t("nav.theme")}</span>
              <ThemeToggle />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{t("nav.language")}</span>
              <LanguageToggle />
            </div>
            {fullscreenSupported && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{t("nav.fullscreen")}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={toggleFullscreen}
                  className="h-9 w-9"
                >
                  {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                </Button>
              </div>
            )}
          </div>

          {/* Sign Out */}
          <div className="px-2 mt-4">
            <Button
              variant="ghost"
              className="justify-start w-full h-11 text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={handleSignOut}
            >
              <LogOut className="h-4 w-4 mr-3" />
              {t("auth.signOut")}
            </Button>
          </div>
        </div>
      </div>

      <LogoutConfirmDialog
        open={showLogoutDialog}
        onOpenChange={setShowLogoutDialog}
        onConfirm={confirmSignOut}
      />
    </>
  );
}

/**
 * @deprecated MobileNav is no longer used as a standalone component.
 * The sidebar now handles mobile navigation via its built-in Sheet.
 * This export is kept for backwards compatibility but renders nothing.
 */
export function MobileNav() {
  return null;
}
